import { useEffect, useRef } from 'react';
import constants from 'core/constants';
import lockr from 'lockr';

interface UseFormCleanupOptions {
    /** Encrypted ID of the draft request. Pass null to disable (e.g. when amending). */
    requestId: string | number | null;
    /** URL prefix identifying the form (with leading + trailing slash). */
    formBasePath: string;
    /** Path segment for the DELETE endpoint, e.g. `access-request`. */
    deleteEndpoint: string;
    /** Redux thunk that DELETEs the draft via axios. Used on SPA unmount. */
    dispatchDelete?: () => void;
}

/**
 * Auto-delete a draft when the user leaves the form without submitting.
 *
 *  - SPA navigation (Back, Dashboard link, etc.): React's effect cleanup runs
 *    when the form component unmounts. If the new URL is no longer inside the
 *    form, dispatch the redux delete thunk (reliable axios DELETE).
 *  - Tab close / full reload: pagehide fires; we send a keepalive fetch
 *    DELETE as best-effort. If the browser drops it, the backend cron
 *    `app:cleanup-stale-draft-requests` removes stale drafts after 24h.
 */
const deleteDraft = (requestId: string | number, deleteEndpoint: string) => {
    try {
        const authToken = lockr.get(constants.AUTH_KEY);
        if (!authToken) {
            return;
        }
        const deleteUrl = `${constants.API_URL}/api/${deleteEndpoint}/${requestId}`;
        fetch(deleteUrl, {
            method: 'DELETE',
            keepalive: true,
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }).catch(() => {
            // Best-effort fire-and-forget — failures are non-fatal.
        });
    } catch {
        // Best-effort fire-and-forget — failures are non-fatal.
    }
};

export const useFormCleanup = ({
    requestId,
    formBasePath,
    deleteEndpoint,
    dispatchDelete,
}: UseFormCleanupOptions) => {
    const requestIdRef = useRef(requestId);
    requestIdRef.current = requestId;
    const formBasePathRef = useRef(formBasePath);
    formBasePathRef.current = formBasePath;
    const deleteEndpointRef = useRef(deleteEndpoint);
    deleteEndpointRef.current = deleteEndpoint;
    const dispatchDeleteRef = useRef(dispatchDelete);
    dispatchDeleteRef.current = dispatchDelete;

    // Delete an abandoned draft only when the user navigates AWAY from it
    // WITHIN the app (SPA unmount to a non-draft URL).
    //
    // Deliberately NOT tied to `pagehide`/`beforeunload`: those also fire on a
    // plain page REFRESH, and at unload time there is no reliable way to tell a
    // refresh from a tab-close. The old pagehide handler therefore deleted the
    // draft on every refresh, so re-fetching after reload 404'd and ejected the
    // user from the form. A reload now keeps the same URL (isStillInThisDraft is
    // true), so the draft survives and the step re-fetches it on mount — the
    // user stays put with their data.
    //
    // Genuine tab-closes are left to the backend's stale-draft cleanup job
    // (drafts idle 24h are removed; guest-booking drafts survive until their
    // link expires) rather than an unreliable unload-time delete.
    useEffect(() => {
        if (!requestId) return undefined;

        return () => {
            // Scope to THIS request id so step-to-step navigation within the
            // same draft (or landing on /visitor-request/new) is treated as
            // "still in the form" and never deletes the draft.
            if (!requestIdRef.current) return;
            const path = window.location.pathname;
            const draftPath = `${formBasePathRef.current}${requestIdRef.current}/`;
            const isStillInThisDraft = path.includes(draftPath);
            if (!isStillInThisDraft) {
                deleteDraft(requestIdRef.current, deleteEndpointRef.current);
            }
        };
    }, [requestId]);
};

export default useFormCleanup;
